<template>
  <v-app v-if="roles" :class="!allowInspectlet && 'inspectletIgnore'" class="itw-app">
    <v-navigation-drawer v-model="drawer" fixed :clipped="clipped" app>
      <v-toolbar class="primary"> </v-toolbar>
      <v-list>
        <v-list-item
          value="true"
          v-for="(item, i) in allowed"
          :key="i"
          :to="{ name: item.to }"
        >
          <v-list-item-action>
            <v-icon v-html="item.icon"></v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title v-text="item.title"></v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar
      app
      fixed
      :clipped-left="clipped"
      :color="apiMode ? 'warning' : 'secondary'"
      class="application--dark"
      dark
    >
      <v-app-bar-nav-icon @click.stop="drawer = !drawer"></v-app-bar-nav-icon>
      <v-toolbar-title class="ml-0">
        <v-row align="end" no-gutters>
          <router-link :to="{ name: 'index' }" style="display: inline-flex">
            <itweb-africa-icon
              v-if="publication == 'africa'"
              size="48"
            ></itweb-africa-icon>
            <itweb-icon v-else height="48" :is-dark="!apiMode"></itweb-icon>
          </router-link>
          <small
            class="ml-2 white--text hidden-md-and-down"
            style="font-family: 'Roboto Slab'; font-size: 24px"
          >
            n.b.
          </small>
        </v-row>
      </v-toolbar-title>
      <v-spacer></v-spacer>

      <v-select
        :items="allowedPublications"
        v-model="publication"
        label="Publication"
        style="max-width: 200px"
        hide-details
        class="mt-2 mr-5"
        :disabled="allowedPublications.length === 1"
      ></v-select>

      <v-menu
        absolute
        right
        offset-x
        :close-on-content-click="false"
        :nudge-width="200"
        :nudge-right="400"
        v-model="menu"
      >
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" fab color="primary" :small="isPhone">
            {{ initials }}
          </v-btn>
        </template>
        <v-card min-width="320">
          <!-- User Info -->
          <v-list dense class="py-0">
            <v-list-item>
              <v-list-item-avatar color="primary">
                <span class="white--text text-h6">{{ initials }}</span>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">{{
                  user.name
                }}</v-list-item-title>
                <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-divider></v-divider>

          <!-- API Mode Section -->
          <v-card flat class="pa-3">
            <div class="text-subtitle-2 mb-2 grey--text text--darken-1">
              <v-icon small class="mr-1">settings</v-icon>
              API Environment
            </div>

            <v-card
              outlined
              :color="apiMode ? 'orange lighten-5' : 'green lighten-5'"
              class="pa-3"
            >
              <v-row align="center" no-gutters>
                <v-col cols="auto">
                  <v-icon :color="apiMode ? 'warning' : 'success'" large>
                    {{ apiMode ? "science" : "cloud_done" }}
                  </v-icon>
                </v-col>
                <v-col class="pl-3">
                  <div
                    class="font-weight-bold"
                    :class="apiMode ? 'warning--text' : 'success--text'"
                  >
                    {{ apiMode ? "Test Mode" : "Production Mode" }}
                  </div>
                  <div class="text-caption grey--text text--darken-1">
                    {{
                      apiMode
                        ? "Stage API (stageapi.itweb.co.za)"
                        : "Production API (api.itweb.co.za)"
                    }}
                  </div>
                </v-col>
              </v-row>

              <v-divider class="my-3"></v-divider>

              <v-switch
                v-model="apiMode"
                color="warning"
                hide-details
                dense
                class="mt-0"
                :disabled="!apiMode && (!stageApiAvailable || checkingStageApi)"
                :loading="checkingStageApi"
              >
                <template v-slot:label>
                  <span class="text-body-2">
                    <span v-if="checkingStageApi">Checking Stage API...</span>
                    <span v-else-if="!apiMode && !stageApiAvailable">
                      Test mode unavailable (Stage API down)
                    </span>
                    <span v-else>
                      Switch to
                      <strong>{{ apiMode ? "Production" : "Test" }}</strong> mode
                    </span>
                  </span>
                </template>
              </v-switch>

              <v-alert dense text type="info" class="mt-3 mb-0 text-caption" icon="info">
                {{
                  apiMode
                    ? "Test mode uses staging data. Changes won't affect production."
                    : "Production mode uses live data. Be careful with changes."
                }}
              </v-alert>

              <v-alert
                dense
                text
                type="warning"
                class="mt-2 mb-0 text-caption"
                icon="logout"
                border="left"
              >
                <strong>Note:</strong> Switching environment will reload the page and you
                will need to log in again.
              </v-alert>
            </v-card>
          </v-card>

          <v-divider></v-divider>

          <!-- Actions -->
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="menu = false">Close</v-btn>
            <v-btn color="error" text @click="logout()">
              <v-icon small left>logout</v-icon>
              Logout.
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </v-app-bar>

    <v-main style="background-color: #f5f6fa; min-height: calc(100vh - 128px)">
      <v-container fluid>
        <v-row
          no-gutters
          class="white"
          v-if="$route.name !== 'events-id-registration-desk' && !hideBreadcrumbs"
        >
          <v-breadcrumbs :items="allBreadcrumbsItems" divider=">">
            <template v-slot:item="props">
              <router-link
                v-if="props.item.to && !props.item.disabled"
                :to="props.item.to"
                class="body-1"
                :class="[props.item.disabled && 'disabled']"
                >{{ props.item.text }}
              </router-link>
              <span class="grey--text body-1" v-else>{{ props.item.text }}</span>
            </template>
          </v-breadcrumbs>
        </v-row>
        <v-divider
          v-if="$route.name !== 'events-id-registration-desk' && !hideBreadcrumbs"
        ></v-divider>
        <div >
          <slot></slot>
        </div>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import itwebIcon from "../components/icons/itweb";
import itwebAfricaIcon from "../components/icons/itweb-africa";
import menu from "../util/menu";
import { checkStageApiAvailability } from "@/utils/apiMode";
import { saveRedirectPath } from "@/utils/redirect";
import { mapState, mapGetters } from "vuex";

export default {
  name: "App",
  props: {
    allowInspectlet: {
      type: Boolean,
      required: false,
      default: false,
    },
    breadcrumbs: {
      type: Array,
      required: false,
      default: () => [],
    },
    hideBreadcrumbs: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    itwebIcon,
    itwebAfricaIcon,
  },
  data: () => ({
    menu: false,
    clipped: false,
    drawer: false,
    copyright:
      "Copyright © 1996 - " +
      new Date().getFullYear() +
      " ITWeb Limited. All rights reserved.",
    stageApiAvailable: true,
    checkingStageApi: false,
  }),
  mounted() {
    this.checkStageApiAvailability();
    // Check every 5 minutes
    this.apiCheckInterval = setInterval(() => {
      this.checkStageApiAvailability();
    }, 5 * 60 * 1000);
  },
  beforeDestroy() {
    if (this.apiCheckInterval) {
      clearInterval(this.apiCheckInterval);
    }
  },
  methods: {
    logout() {
      this.$store.dispatch("user/logout").then(() => {
        localStorage.removeItem("itw_access_token");
        this.$router.replace({ path: "/login" });
      });
    },
    async checkStageApiAvailability() {
      this.checkingStageApi = true;
      try {
        this.stageApiAvailable = await checkStageApiAvailability();

        // If in test mode and stage API is down, switch back to production
        if (!this.stageApiAvailable && this.$store.state.publication.apiMode === "test") {
          this.$store.commit("publication/setApiMode", "production");
          this.$store.commit("snackbar/setSnackbarColor", "error");
          this.$store.commit(
            "snackbar/setSnackbarText",
            "Stage API is not available. Switched to Production mode."
          );
          this.$store.commit("snackbar/setSnackbarDisplay", true);
          saveRedirectPath(this.$route.fullPath);
          window.location.reload();
        }
      } finally {
        this.checkingStageApi = false;
      }
    },
  },
  computed: {
    ...mapState({ user: "user" }),
    ...mapGetters({ initials: "user/initials" }),
    ...mapGetters({ roles: "user/roles" }),
    ...mapGetters({ can: "user/can" }),
    ...mapGetters({ allowedPublications: "user/publications" }),
    allowed() {
      return menu.links.filter(
        (link) =>
          this.can(link.permissions) &&
          (link.publication == this.publication || !link.publication)
      );
    },
    allBreadcrumbsItems() {
      let home = {
        text: "Home",
        disabled: this.$route.name === "index",
        to: { name: "index" },
      };
      return [home, ...this.breadcrumbs];
    },
    publication: {
      get() {
        return this.$store.state.publication.type;
      },
      set(value) {
        this.$store.commit("publication/setPublication", value);
        saveRedirectPath(this.$route.fullPath);
        window.location.reload();
      },
    },
    apiMode: {
      get() {
        return this.$store.state.publication.apiMode === "test";
      },
      set(value) {
        const mode = value ? "test" : "production";

        // If switching to test mode, check if stage API is available
        if (mode === "test" && !this.stageApiAvailable) {
          this.$store.commit("snackbar/setSnackbarColor", "warning");
          this.$store.commit(
            "snackbar/setSnackbarText",
            "Stage API is currently unavailable. Please try again later."
          );
          this.$store.commit("snackbar/setSnackbarDisplay", true);
          return;
        }

        this.$store.commit("publication/setApiMode", mode);
        saveRedirectPath(this.$route.fullPath);
        window.location.reload();
      },
    },
    canSwitchToTestMode() {
      return this.stageApiAvailable && !this.checkingStageApi;
    },
  },
};
</script>

<style scoped>
.test-mode-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  border-radius: 0 !important;
}
</style>
